import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useMutation } from '@apollo/client'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { usePageData } from './usePageData'
import { useFormValidation } from './useFormValidation'
import { useAdCreativeProcessor } from './useAdCreativeProcessor'
import { CREATE_LEAD_FORM, UPDATE_LEAD_FORM } from '@/graphql/media-buying-queries'
import { showNotification } from '@/utils/notifications'
import { z } from '@/utils/myZodLite'
import { isEmpty } from 'lodash'
import { isMetaOwnedUrl, META_OWNED_URL_MESSAGE } from '@/utils/lead-form-url'

export const useFormThankYou = () => {
    const {
        getStepContentData,
        setStepContentData,
        setChildrenVisibility,
        stepOrder,
        markStepAs,
        clearStepAndChildren,
        recreateStepOrder,
    } = useWizard()
    const { connectedPublishers } = useMediaBuyingContext()

    // Instant form creation state
    const [isCreatingInstantForm, setIsCreatingInstantForm] = useState(false)
    const [refreshProcessor, setRefreshProcessor] = useState(0)

    const adCreativeProcessor = useAdCreativeProcessor(refreshProcessor)
    const [createLeadForm, { loading: creatingForm }] = useMutation(CREATE_LEAD_FORM)
    const [updateLeadForm, { loading: updatingForm }] = useMutation(UPDATE_LEAD_FORM)

    const { pageIcon, pageTitle, pageLink, publisherId, pageId, campaignId } = usePageData(connectedPublishers)

    // Form state
    const [thankYouMessage, setThankYouMessage] = useState("Thanks, you're all set.")
    const [description, setDescription] = useState('You can visit our website or exit from the form now.')
    const [websiteButtonText, setWebsiteButtonText] = useState('View website')
    const [websiteUrl, setWebsiteUrl] = useState('')
    const hasHydratedInitialValues = useRef(false)

    // Validation schema
    const FormSchema = useMemo(
        () =>
            z.object(
                {
                    thankYouMessage: z.string(),
                    description: z.optional(z.string()),
                    websiteButtonText: z.optional(z.string()),
                    websiteUrl: z.string(),
                },
                (data) => {
                    const errors = []

                    // Validate thank you message
                    if (isEmpty(data.thankYouMessage)) {
                        errors.push({ message: 'Headline is required.', path: ['thankYouMessage'] })
                    }

                    // Validate URL is required
                    if (isEmpty(data.websiteUrl)) {
                        errors.push({ message: 'URL is required.', path: ['websiteUrl'] })
                    } else if (!data.websiteUrl.match(/^https?:\/\/.+/)) {
                        errors.push({
                            message: 'Please enter a valid URL starting with http:// or https://',
                            path: ['websiteUrl'],
                        })
                    } else if (isMetaOwnedUrl(data.websiteUrl)) {
                        errors.push({ message: META_OWNED_URL_MESSAGE, path: ['websiteUrl'] })
                    } else {
                        // Validate URL format
                        try {
                            new URL(data.websiteUrl)
                        } catch {
                            errors.push({
                                message: 'Please enter a valid URL.',
                                path: ['websiteUrl'],
                            })
                        }
                    }

                    return errors.length ? errors : true
                },
            ),
        [],
    )

    const { formErrors, validateForm: validate, hasErrors } = useFormValidation(FormSchema)

    const formData = useMemo(
        () => ({
            thankYouMessage,
            description,
            websiteButtonText,
            websiteUrl,
        }),
        [thankYouMessage, description, websiteButtonText, websiteUrl],
    )

    const validateForm = useCallback(
        (silent = false) => {
            return validate(formData, silent)
        },
        [validate, formData],
    )

    const completeFormSubmission = useCallback(
        (leadFormData, form, message) => {
            clearStepAndChildren('leadForm')
            setChildrenVisibility('leadForm', false)
            setStepContentData(
                {
                    ...leadFormData,
                    selectedForm: form,
                    showNewLeadForm: false,
                    editingFormId: null,
                    meta_form_id: form.id,
                    formCreationTimestamp: new Date().toISOString(),
                },
                'leadForm',
            )
            const stepIndex = stepOrder.findIndex((step) => step === 'leadForm')
            if (stepIndex !== -1) {
                markStepAs(1, stepIndex)
            }
            showNotification(message, 'success')
            setTimeout(() => {
                recreateStepOrder('uploadAssets')
            }, 0)
        },
        [clearStepAndChildren, setChildrenVisibility, setStepContentData, stepOrder, markStepAs, recreateStepOrder],
    )

    const submitForm = useCallback(async () => {
        const leadFormData = getStepContentData('leadForm') || {}
        const isCreatingNewForm = leadFormData.showNewLeadForm !== false
        const editingFormId = leadFormData.editingFormId
        const selectedFormStatus = leadFormData?.selectedForm?.status || null
        const canUpdateDraftForm = Boolean(editingFormId && selectedFormStatus === 'DRAFT')

        if (isCreatingNewForm && canUpdateDraftForm && publisherId) {
            try {
                setIsCreatingInstantForm(true)
                setRefreshProcessor((prev) => prev + 1)

                const instantFormData = adCreativeProcessor.createInstantForm()

                const result = await updateLeadForm({
                    variables: {
                        publisherId,
                        formId: editingFormId,
                        pageId,
                        instantForm: instantFormData,
                        campaignId,
                    },
                })

                const updatedForm = result?.data?.updateLeadForm
                if (!updatedForm) {
                    throw new Error('Updated lead form data was not returned')
                }

                completeFormSubmission(leadFormData, updatedForm, 'Instant form updated successfully!')
            } catch (error) {
                showNotification(
                    'Failed to update instant form. Please try again.',
                    'error',
                    3000,
                    null,
                    null,
                    true,
                    true,
                )
                return false
            } finally {
                setIsCreatingInstantForm(false)
            }
        }

        if (isCreatingNewForm && !canUpdateDraftForm && publisherId) {
            try {
                setIsCreatingInstantForm(true)

                setRefreshProcessor((prev) => prev + 1)

                const instantFormData = Object.assign(adCreativeProcessor.createInstantForm(), {
                    ...(websiteUrl && {
                        follow_up_action_url: websiteUrl,
                    }),
                    thank_you_page: {
                        title: thankYouMessage || '',
                        ...(description && {
                            body: description,
                        }),
                        button_type: websiteUrl || websiteButtonText ? 'VIEW_WEBSITE' : 'NONE',
                        ...(websiteUrl && {
                            website_url: websiteUrl,
                        }),
                        ...(websiteButtonText && {
                            button_text: websiteButtonText,
                        }),
                    },
                })
                if (instantFormData) {
                    const result = await createLeadForm({
                        variables: {
                            publisherId,
                            pageId,
                            instantForm: instantFormData,
                            campaignId,
                        },
                    })

                    const createdForm = {
                        id: result.data.createLeadForm.id,
                        name: result.data.createLeadForm.name,
                        status: result.data.createLeadForm.status,
                        created_time: result.data.createLeadForm.created_time,
                        privacy_policy_url: result.data.createLeadForm.privacy_policy_url,
                        questions: result.data.createLeadForm.questions || [],
                    }

                    completeFormSubmission(leadFormData, createdForm, 'Instant form created successfully!')
                }
            } catch (error) {
                showNotification(
                    'Failed to create instant form. Please try again.',
                    'error',
                    3000,
                    null,
                    null,
                    true,
                    true,
                )
                return false
            } finally {
                setIsCreatingInstantForm(false)
            }
        }

        if (isCreatingNewForm && !publisherId) {
            if (!connectedPublishers || Object.keys(connectedPublishers).length === 0) {
                showNotification('Please wait for publisher data to load, then try again.', 'warning')
            } else {
                showNotification('Can not create form as publisherId is missing', 'error')
            }
            return false
        }

        return true
    }, [
        getStepContentData,
        publisherId,
        websiteUrl,
        thankYouMessage,
        description,
        websiteButtonText,
        adCreativeProcessor,
        createLeadForm,
        updateLeadForm,
        pageId,
        completeFormSubmission,
        connectedPublishers,
    ])

    // Load saved data once to avoid overwriting user typing on re-renders.
    useEffect(() => {
        if (hasHydratedInitialValues.current) return
        const saved = getStepContentData() || {}
        setThankYouMessage(saved.thankYouMessage || "Thanks, you're all set.")
        setDescription(saved.description || 'You can visit our website or exit from the form now.')
        setWebsiteButtonText(saved.websiteButtonText || 'View website')
        setWebsiteUrl(saved.websiteUrl || '')
        hasHydratedInitialValues.current = true
    }, [getStepContentData])

    // Save data on changes using memoized form data
    useEffect(() => {
        setStepContentData(formData)
    }, [setStepContentData, formData, refreshProcessor])

    // Field change handlers with inline validation
    const handleThankYouMessageChange = useCallback(
        (val) => {
            setThankYouMessage(val)
            if (hasErrors) {
                validateForm(true)
            }
        },
        [hasErrors, validateForm],
    )

    const handleDescriptionChange = useCallback(
        (e) => {
            setDescription(e.target.value)
            if (hasErrors) {
                validateForm(true)
            }
        },
        [hasErrors, validateForm],
    )

    const handleWebsiteButtonTextChange = useCallback(
        (val) => {
            setWebsiteButtonText(val)
            if (hasErrors) {
                validateForm(true)
            }
        },
        [hasErrors, validateForm],
    )

    const handleWebsiteUrlChange = useCallback(
        (val) => {
            setWebsiteUrl(val)
            if (hasErrors) {
                validateForm(true)
            }
        },
        [hasErrors, validateForm],
    )

    const thankYouQuestion = useMemo(
        () => ({
            id: 'thank-you-screen',
            thankyou: true,
            thankYouMessage,
            description,
            websiteButtonText,
            websiteUrl,
        }),
        [thankYouMessage, description, websiteButtonText, websiteUrl],
    )

    const isFormProcessing = creatingForm || updatingForm || isCreatingInstantForm

    return {
        // Form state
        thankYouMessage,
        description,
        websiteButtonText,
        websiteUrl,
        // Field handlers
        handleThankYouMessageChange,
        handleDescriptionChange,
        handleWebsiteButtonTextChange,
        handleWebsiteUrlChange,
        // Validation
        formErrors,
        hasErrors,
        validateForm,
        // Submission
        submitForm,
        isFormProcessing,
        // Computed values
        thankYouQuestion,
        // Page data
        pageIcon,
        pageTitle,
    }
}
